import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, TextField, MenuItem, Grid, Card, CardContent,
  Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Tooltip
} from '@mui/material';
import {
  Security as SecurityIcon, Refresh as RefreshIcon, WarningAmber as WarnIcon,
  CheckCircle as OkIcon, SmartToy as AiIcon, MarkEmailRead as EmailIcon
} from '@mui/icons-material';
import { Navigate } from 'react-router-dom';
import {
  getLoginHistory, getLoginHistoryAiReport, markLoginHistoryReviewed
} from '../../services/api';
import { isSuperAdmin } from '../../utils/auth';

const riskColor = (level) => {
  if (level === 'critical') return 'error';
  if (level === 'high') return 'warning';
  if (level === 'medium') return 'info';
  return 'success';
};

const statusColor = (status) => {
  if (status === 'success') return 'success';
  if (status === 'unauthorized') return 'error';
  return 'warning';
};

const LoginHistoryPage = () => {
  const allowed = isSuperAdmin();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [ai, setAi] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    unauthorized: '',
    risk_level: 'all',
    email: '',
    ip: '',
    from: '',
    to: ''
  });

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    setError('');
    try {
      const [hist, report] = await Promise.all([
        getLoginHistory({
          page: page + 1,
          limit: rowsPerPage,
          status: filters.status,
          unauthorized: filters.unauthorized,
          risk_level: filters.risk_level,
          email: filters.email || undefined,
          ip: filters.ip || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined
        }),
        getLoginHistoryAiReport().catch(() => null)
      ]);
      if (hist?.success) {
        setRows(hist.data?.rows || []);
        setTotal(hist.data?.pagination?.total || 0);
        setSummary(hist.data?.summary || null);
      } else {
        setError(hist?.message || 'Failed to load login history');
      }
      if (report?.success) setAi(report.data);
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, allowed]);

  useEffect(() => { load(); }, [load]);

  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleReview = async (id) => {
    try {
      await markLoginHistoryReviewed(id);
      load();
      setDetail(null);
    } catch (err) {
      setError(err.message || 'Failed to mark reviewed');
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon /> Login History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Super Admin only · AI security monitoring · unauthorized alerts
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Successful logins', value: summary?.totalSuccess ?? '—', color: '#2e7d32' },
          { label: 'Failed / blocked', value: summary?.totalFailed ?? '—', color: '#ed6c02' },
          { label: 'Unauthorized', value: summary?.totalUnauthorized ?? '—', color: '#c62828' },
          { label: 'High risk (24h)', value: summary?.highRisk24h ?? '—', color: '#6a1b9a' }
        ].map((k) => (
          <Grid item xs={12} sm={6} md={3} key={k.label}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: k.color }}>{k.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {ai && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <AiIcon color="primary" />
            <Typography fontWeight={700}>AI Security Control</Typography>
            <Chip
              size="small"
              color={riskColor(ai.threatLevel)}
              label={`Threat ${ai.threatLevel} · score ${ai.threatScore}`}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Last 7 days · {ai.totals?.attempts || 0} attempts · {ai.totals?.unauthorized || 0} unauthorized
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Recommendations</Typography>
              {(ai.recommendations || []).map((r, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>• {r}</Typography>
              ))}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Top attack IPs</Typography>
              {(ai.topAttackIps || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">None</Typography>
              )}
              {(ai.topAttackIps || []).map((x) => (
                <Chip key={x.ip} size="small" label={`${x.ip} (${x.count})`} sx={{ mr: 0.5, mb: 0.5 }} />
              ))}
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField select size="small" label="Status" value={filters.status}
            onChange={(e) => { setPage(0); setFilters({ ...filters, status: e.target.value }); }}
            sx={{ minWidth: 140 }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="success">Success</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="unauthorized">Unauthorized</MenuItem>
          </TextField>
          <TextField select size="small" label="Unauthorized" value={filters.unauthorized}
            onChange={(e) => { setPage(0); setFilters({ ...filters, unauthorized: e.target.value }); }}
            sx={{ minWidth: 140 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">Unauthorized only</MenuItem>
          </TextField>
          <TextField select size="small" label="Risk" value={filters.risk_level}
            onChange={(e) => { setPage(0); setFilters({ ...filters, risk_level: e.target.value }); }}
            sx={{ minWidth: 120 }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
          </TextField>
          <TextField size="small" label="Email" value={filters.email}
            onChange={(e) => setFilters({ ...filters, email: e.target.value })} />
          <TextField size="small" label="IP" value={filters.ip}
            onChange={(e) => setFilters({ ...filters, ip: e.target.value })} />
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
          <Button variant="contained" onClick={() => { setPage(0); load(); }}>Apply</Button>
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date / Time</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>MAC / Device ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Risk</TableCell>
                  <TableCell>After login</TableCell>
                  <TableCell>Alert</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ bgcolor: r.is_unauthorized ? 'rgba(198,40,40,0.06)' : undefined }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.created_at ? new Date(r.created_at).toLocaleString('en-GB') : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.username || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.email || '—'}</Typography>
                      {r.role && <Chip size="small" label={r.role} sx={{ ml: 0.5, height: 18 }} />}
                    </TableCell>
                    <TableCell>{r.ip_address || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 160 }}>{r.location || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 120, fontSize: 11 }}>{r.mac_address || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColor(r.login_status)} label={r.login_status} />
                      {r.is_unauthorized && (
                        <Chip size="small" color="error" icon={<WarnIcon />} label="Unauthorized" sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={riskColor(r.risk_level)} label={`${r.risk_level} (${r.risk_score})`} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 140 }}>
                      {(r.post_login_actions || []).length
                        ? `${r.post_login_actions.length} action(s)`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {r.email_alert_sent ? (
                        <Tooltip title="Email alert sent">
                          <EmailIcon color="warning" fontSize="small" />
                        </Tooltip>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => setDetail(r)}>Details</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">No login records yet</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Paper>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>Login detail #{detail?.id}</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={1.2}>
              <Typography><b>Date:</b> {new Date(detail.created_at).toLocaleString('en-GB')}</Typography>
              <Typography><b>User:</b> {detail.username || '—'} ({detail.email || '—'}) · {detail.role || '—'}</Typography>
              <Typography><b>IP:</b> {detail.ip_address}</Typography>
              <Typography><b>MAC / Device ID:</b> {detail.mac_address}</Typography>
              <Typography><b>Location:</b> {detail.location}</Typography>
              <Typography><b>Login URL:</b> {detail.login_url || '—'}</Typography>
              <Typography><b>Device:</b> {detail.device_info || '—'}</Typography>
              <Typography><b>User agent:</b> <span style={{ fontSize: 12 }}>{detail.user_agent || '—'}</span></Typography>
              <Typography><b>Status:</b> {detail.login_status} {detail.is_unauthorized ? '· UNAUTHORIZED' : ''}</Typography>
              <Typography><b>Failure reason:</b> {detail.failure_reason || '—'}</Typography>
              <Divider />
              <Typography fontWeight={700}>AI analysis</Typography>
              <Typography><b>Score:</b> {detail.risk_score} ({detail.risk_level})</Typography>
              <Typography><b>Summary:</b> {detail.ai_summary || '—'}</Typography>
              <Box>
                {(detail.ai_flags || []).map((f) => (
                  <Chip key={f} size="small" label={f} sx={{ mr: 0.5, mb: 0.5 }} color="warning" />
                ))}
              </Box>
              <Divider />
              <Typography fontWeight={700}>What they did after login</Typography>
              {(detail.post_login_actions || []).length === 0 && (
                <Typography color="text.secondary">No activity recorded yet</Typography>
              )}
              {(detail.post_login_actions || []).map((a, i) => (
                <Typography key={i} variant="body2">
                  {a.at ? new Date(a.at).toLocaleString('en-GB') : ''} — {a.method || 'GET'} {a.action}
                </Typography>
              ))}
              {detail.reviewed && (
                <Alert severity="success" icon={<OkIcon />}>
                  Reviewed {detail.reviewed_at ? new Date(detail.reviewed_at).toLocaleString('en-GB') : ''}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
          {detail && !detail.reviewed && (
            <Button variant="contained" onClick={() => handleReview(detail.id)}>Mark reviewed</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginHistoryPage;
